import React, {type FC} from 'react';
import {Formik} from 'formik';
import {Alert, AlertIcon, Box} from '@tempo/core';
import {type NumberSchema, type StringSchema, object} from 'yup';
import '@app/modules/yup/addMethods';
import {
	useAgeSchema,
	useIdentitySchema,
	usePasswordSchema,
} from '@app/hooks/useSchema';
import {Form} from '@app/components/Form/Form';
import {useForm} from './useForm';
import PasswordField from './PasswordField';
import BirthdayField from './BirthdayField';
import IdentityField from './IdentityField';
import {useTranslation} from '@app/hooks/useTranslation';
import {logClick} from '@app/modules/tracking/gtm';
import {Button} from '../BtnWrapper';
import {TextWithLink} from '../TextWithLink';

interface Props {
	missingInfos: string[];
}

const MissingInfoForm: FC<Props> = ({missingInfos}) => {
	const {errorMessage, isLoading, onSubmit} = useForm();
	const t = useTranslation();
	const [passwordSchema] = usePasswordSchema({context: 'reg'});
	const [birthdaySchema] = useAgeSchema();
	const [sexSchema] = useIdentitySchema();
	const ctaLabel = t('common.action_finalizemyaccount');

	return (
		<Formik
			initialValues={missingInfos.reduce((val, acc) => {
				return {...val, [acc]: ''};
			}, {})}
			onSubmit={(values) => {
				logClick({
					eventaction: 'submit-complete-account',
				});
				onSubmit(values);
			}}
			validationSchema={object().shape(
				missingInfos.reduce((schema, fieldName) => {
					const newSchema: Record<string, StringSchema | NumberSchema> = {
						...schema,
					};
					const schemaMap = {
						password: passwordSchema,
						birthday: birthdaySchema,
						sex: sexSchema,
					};
					const fieldSchema = schemaMap[fieldName as keyof typeof schemaMap];
					if (fieldSchema) {
						newSchema[fieldName] = fieldSchema;
					}

					return newSchema;
				}, {}),
			)}
			validateOnBlur={true}
			validateOnChange={true}
		>
			<Form>
				{missingInfos.map((missingInfo) => (
					<Box key={missingInfo} w="100%">
						{missingInfo === 'password' && <PasswordField />}
						{missingInfo === 'birthday' && <BirthdayField />}
						{missingInfo === 'sex' && <IdentityField />}
					</Box>
				))}
				<Button
					data-testid="finalize-my-account-button"
					type="submit"
					width="100%"
					isLoading={isLoading}
				>
					{ctaLabel}
				</Button>

				<TextWithLink
					dangerouslySetInnerHTML={{
						__html: t('formal.legalmention', {
							sprintf: [
								ctaLabel,
								'href="https://www.deezer.com/legal/cgu" target="_blank" rel="noreferrer" data-testid="terms"',
								'href="https://www.deezer.com/legal/personal-datas" target="_blank" rel="noreferrer" data-testid="privacy-policy"',
							],
						}),
					}}
				/>

				{errorMessage && (
					<Alert status="error" data-testid="api-error-message">
						<AlertIcon />
						{errorMessage}
					</Alert>
				)}
			</Form>
		</Formik>
	);
};

export default MissingInfoForm;
